/**
 * Équipes — liste groupée par organisation (217:4531, décision T1 du 2026-09-27).
 *
 * Chaque groupe s'ouvre sur une `BandeRupture` (« <Org> · votre rôle : <rôle> » — rôle
 * D'ORGANISATION, `roleBadge.*`, distinct du rôle d'équipe affiché sur chaque carte,
 * `roles.*`), puis les cellules du groupe sur 4 colonnes : une carte par équipe (nom, rôle en
 * `Tag`, « N membres · N projets », « Ouvrir ») et une cellule « + Nouvelle équipe » par
 * groupe — cellule ordinaire portant un bouton fantôme, SANS hachure : depuis `@exidis/ui`
 * 1.3.0, la hachure est réservée aux états vides, et la bande de groupe n'en porte plus non
 * plus (`BandeRupture` 1.3.0, bande simple).
 *
 * Planche B (`@exidis/ui` 1.5.0, reference.md §11.7) : UNE seule grille `.cells` pour toute la
 * page, gouttière de 16 px portée par le paquet. Chaque groupe s'y ouvre sur sa bande, rangée
 * libre en `col-span-full` (eyebrow sur le fond de page, sans cadre) : elle occupe une rangée
 * entière, la première carte du groupe repart donc d'elle-même en colonne 1. Plus de bande
 * bornée aux cellules du groupe ni de `col-start-1` forcé — ces calculs n'existaient que pour
 * que la bande encadrée de la planche D ne dépasse pas une planche bornée. La gestion d'une équipe (membres, adhésion, projets) a quitté cette page pour
 * `/equipes/:teamId` (`TeamDetailPage`) — cette liste ne fait plus qu'orienter.
 */

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BandeRupture } from '@exidis/ui/composants';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import {
  Empty,
  LoadError,
  PageBody,
  PageHeader,
  PrimaryButton,
  TruncatedListWarning,
} from '../components/common.jsx';
import { TeamCard } from '../components/TeamCard.jsx';
import { CreateTeamDialog } from '../dialogs/CreateTeamDialog.jsx';
import { BTN_GHOST_SM, CELLS_PLANCHE, CELL_PLANCHE } from '../ui/theme.js';

/** Mêmes clés que `RoleBadge` — rôle D'ORGANISATION affiché sur la bande de rupture. */
const ORG_ROLE_LABEL_KEYS = {
  proprietaire: 'roleBadge.proprietaire',
  administrateur: 'roleBadge.administrateur',
  facturation: 'roleBadge.facturation',
  editeur: 'roleBadge.editeur',
  lecteur: 'roleBadge.lecteur',
};

/**
 * Regroupe les équipes par organisation, dans l'ordre de première apparition — sauf l'espace
 * personnel (`organizationKind === 'personnel'`) qui passe toujours en premier (ORG-8) : il ne
 * s'affiche jamais sous un nom d'organisation.
 */
function groupByOrganization(teams) {
  const order = [];
  const byOrg = new Map();
  for (const team of teams) {
    if (!byOrg.has(team.organizationId)) {
      order.push(team.organizationId);
      byOrg.set(team.organizationId, {
        organizationId: team.organizationId,
        organizationKind: team.organizationKind,
        teams: [],
      });
    }
    byOrg.get(team.organizationId).teams.push(team);
  }
  const groups = order.map((id) => byOrg.get(id));
  return groups.sort((a, b) => {
    const aPersonal = a.organizationKind === 'personnel' ? 0 : 1;
    const bPersonal = b.organizationKind === 'personnel' ? 0 : 1;
    return aPersonal - bPersonal;
  });
}

export function TeamsPage() {
  const { t } = useTranslation();
  // `null` = modale fermée ; sinon `{ organizationId }`, `''` désignant l'espace personnel
  // (le serveur y place l'équipe quand `organizationId` est absent du corps de la requête).
  const [creating, setCreating] = useState(null);

  const {
    data: teamsData,
    error,
    loading,
    reload: reloadTeams,
  } = useAsync((signal) => fetchAllPages('/teams', { signal }), []);
  const teams = teamsData?.data ?? [];

  const { data: organizationsData } = useAsync(
    (signal) => api.get('/me/organizations', { signal }),
    [],
  );
  const organizations = organizationsData?.data ?? [];
  const orgRoleById = new Map(
    organizations.map((organization) => [organization.id, organization.role]),
  );

  const groups = groupByOrganization(teams);

  const onCreated = () => {
    setCreating(null);
    reloadTeams();
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('teams.eyebrow')}
        title={t('teams.title')}
        subtitle={t('teams.subtitle')}
        primary={
          // L'espace personnel existe toujours : le bouton n'est plus conditionné à la présence
          // d'une organisation (ORG-8). Il ouvre la modale sur « Mon espace » par défaut.
          <PrimaryButton onClick={() => setCreating({ organizationId: '' })}>
            {t('teams.newTeam')}
          </PrimaryButton>
        }
      />

      {error && (
        <PageBody className="p-4">
          <LoadError error={error} onRetry={reloadTeams} />
        </PageBody>
      )}
      {teamsData?.truncated && (
        <PageBody className="p-4">
          <TruncatedListWarning />
        </PageBody>
      )}

      <PageBody className="p-4">
        {loading ? (
          <p className="text-sm text-ink-soft">{t('teams.loading')}</p>
        ) : error ? null : teams.length === 0 ? (
          <Empty>{t('teams.empty')}</Empty>
        ) : (
          <div className={`${CELLS_PLANCHE} grid-cols-1 sm:grid-cols-2 lg:grid-cols-4`}>
            {groups.map((group) => {
              const first = group.teams[0];
              const isPersonal = group.organizationKind === 'personnel';
              return (
                <Fragment key={group.organizationId}>
                  <BandeRupture
                    libelle={
                      isPersonal
                        ? t('space.personal')
                        : t('teams.groupRole', {
                            organization: first.organizationName,
                            role: t(
                              ORG_ROLE_LABEL_KEYS[orgRoleById.get(group.organizationId)] ??
                                'roleBadge.lecteur',
                            ),
                          })
                    }
                  />
                  {group.teams.map((team) => (
                    <TeamCard key={team.id} team={team} />
                  ))}
                  <div
                    className={`${CELL_PLANCHE} flex min-h-[7rem] items-center justify-center bg-paper p-4`}
                  >
                    <span className="ticks" aria-hidden="true" />
                    <button
                      type="button"
                      className={BTN_GHOST_SM}
                      onClick={() =>
                        setCreating({ organizationId: isPersonal ? '' : group.organizationId })
                      }
                    >
                      {t('teams.newTeamCell')}
                    </button>
                  </div>
                </Fragment>
              );
            })}
          </div>
        )}
      </PageBody>

      {creating && (
        <CreateTeamDialog
          organizations={organizations}
          defaultOrganizationId={creating.organizationId}
          onClose={() => setCreating(null)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}
